'use client';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import { useEventCallback } from '@susu/custom-made/hooks/useEventCallback';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { applyBusinessRulesForStylePresetSelection } from '@susu/custom-made/utils/businessRules';
import { Clients } from '@susu/custom-made/utils/constants';
import type {
  BusinessRules,
  OptionConfigurationType,
  StylePreset,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection, Section } from '@susu/custom-made/utils/navigation';
import {
  type ConfigurationTypes,
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
} from '@susu/custom-made/utils/productDefinition';
import { memo, useEffect } from 'react';

const { SHOW_LOGS } = FEATURE_TOGGLES;

export const SelectionEffects = memo(function SelectionEffects({
  options,
  optionValues,
  businessRules,
  matchingProductPresets,
  isInPreloadFlow,
  isDatadogReady,
}: Readonly<{
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  businessRules: BusinessRules;
  matchingProductPresets: StylePreset[];
  isInPreloadFlow: boolean;
  isDatadogReady: boolean;
}>) {
  const { section, setIsFinishButtonDisabled } = useNavigationContext();
  const { client, product } = useUserContext();
  const { areAllConfigurationMissingValuesEmpty } = useMissingValues();
  const { selectedFabric } = useFabricContext();
  const {
    selectedOptions,
    hiddenOptionsAndValues,
    selectedBaseStyle,
    selectedMonogram,
    updateHiddenOptionsAndValues,
    selectOption,
    clearMonograms,
    setIsBaseStyleSelection,
    selectBaseStyle,
  } = useOptionContext();
  const showLogsFeatureEnabled = useFeatureIsOn(SHOW_LOGS);

  // Keep the Finish button's disabled state in sync so both the mobile/tablet
  // PreviewHeader button and the desktop Navigation button share one source of truth.
  useEffect(() => {
    setIsFinishButtonDisabled(!areAllConfigurationMissingValuesEmpty());
  }, [areAllConfigurationMissingValuesEmpty, setIsFinishButtonDisabled]);

  const reportInitialLoad = useEventCallback(() => {
    window.DD_RUM?.addAction('Custom made - Initial Load', {
      customData: {
        selectedFabric,
        selectedOptions,
        hiddenOptionsAndValues,
      },
    });
  });

  useEffect(() => {
    if (isDatadogReady) reportInitialLoad();
  }, [isDatadogReady, reportInitialLoad]);

  // In-store skips the picker entirely: the default (first-in-list) preset is applied
  // automatically, same as it would be if the adviser had confirmed it on the picker.
  const applyDefaultPreset = useEventCallback(
    (configurationTypeId: ConfigurationTypes, defaultStylePreset: StylePreset) => {
      const baseDefinitionConfigurationTypeId =
        getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
      const presetOptions = options[baseDefinitionConfigurationTypeId]?.items;
      if (!selectedFabric || !presetOptions) return;

      const tmpSelectedOptions = structuredClone(selectedOptions);
      const tmpHiddenOptionsAndValues = structuredClone(hiddenOptionsAndValues);

      applyBusinessRulesForStylePresetSelection(
        defaultStylePreset,
        tmpSelectedOptions,
        selectedFabric.id,
        businessRules,
        tmpHiddenOptionsAndValues,
        optionValues,
        configurationTypeId,
        presetOptions,
        product!,
        true,
        showLogsFeatureEnabled,
      );

      updateHiddenOptionsAndValues(tmpHiddenOptionsAndValues);
      selectOption(tmpSelectedOptions);
      selectBaseStyle(configurationTypeId, defaultStylePreset);
    },
  );

  useEffect(() => {
    if (!section || section === Section.Fabric || section === Section.Summary || isInPreloadFlow) return;

    const configurationTypeId = getConfigurationTypeBySection(section);
    if (selectedBaseStyle[configurationTypeId] || matchingProductPresets.length === 0) return;

    if (client === Clients.inStore) {
      applyDefaultPreset(configurationTypeId, matchingProductPresets[0]);
    } else {
      setIsBaseStyleSelection(true);
    }
  }, [
    section,
    selectedBaseStyle,
    matchingProductPresets,
    isInPreloadFlow,
    client,
    selectedFabric,
    applyDefaultPreset,
    setIsBaseStyleSelection,
  ]);

  // Clear monogram in case monogram color is hidden
  useEffect(() => {
    if (hiddenOptionsAndValues && selectedMonogram) {
      const hiddenColors = new Set(hiddenOptionsAndValues.monogramColors);
      if (Object.values(selectedMonogram).some(monogram => hiddenColors.has(monogram.colorId))) {
        clearMonograms();
      }
    }
  }, [hiddenOptionsAndValues, selectedMonogram, clearMonograms]);

  return null;
});
